import { TicketCommentDisposition } from "@mg/schemas/src/commons/ticket";
import { ChatTeardropText, FunnelSimple } from "@phosphor-icons/react";
import { DropdownMenu, Flex, Heading, IconButton } from "@radix-ui/themes";
import { type Editor } from "@tldraw/tldraw";
import cx from "classnames";
import { useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";

import { RepliesCollapseContext } from "./Comment";
import { Comments } from "./Comments";

import { Sidebar } from "../../../../../components/sidebar/Sidebar";
import { SidebarContent } from "../../../../../components/sidebar/SidebarContent";
import { SidebarHeader } from "../../../../../components/sidebar/SidebarHeader";
import { SidebarProvider } from "../../../../../components/sidebar/SidebarProvider";
import { SidebarRail } from "../../../../../components/sidebar/SidebarRail";
import { useSidebar } from "../../../../../components/sidebar/useSidebar";
import { useAnalytics } from "../../../../../utils/analytics";
import {
  canShowDismissedComments,
  canShowDeletedComments,
} from "../../../../../utils/auth";
import { isNil } from "../../../../../utils/fp";
import { useAppDispatch, useAppSelector } from "../../../../../utils/hooks";
import {
  setDrawerOpen,
  toggleShowDismissedComments,
  toggleShowResolvedComments,
  toggleShowDeletedComments,
} from "../../../../../utils/slices/ui";

type CommentDrawerProps = {
  editor?: Editor | null;
  videoRef: React.RefObject<{
    handleCommentClicked: (_seekTime: number) => void;
    duration: number;
  }>;
};

export function CommentDrawer(props: CommentDrawerProps) {
  const analytics = useAnalytics("CommentsSidebar");

  const dispatch = useAppDispatch();
  const ticket = useAppSelector((state) => state.ticket.value);

  const reversedVersionIndex = useAppSelector(
    (state) => state.ticket.reversedVersionIndex,
  );
  const versions = useAppSelector((state) => state.ticket.revisions);
  const sidebarOpen = useAppSelector((state) => state.ui.drawerOpen.comments);

  const currentVersion = useMemo(() => {
    if (isNil(reversedVersionIndex)) return undefined;
    return versions[reversedVersionIndex];
  }, [reversedVersionIndex, versions]);

  if (isNil(ticket)) {
    return null;
  }

  if (currentVersion?.errorDetails?.reason) {
    return null;
  }

  return (
    <SidebarProvider
      name="comments"
      open={sidebarOpen}
      onOpenChange={(open) => {
        analytics.capture(`comments_sidebar_${open ? "opened" : "closed"}`);
        dispatch(setDrawerOpen({ comments: open }));
      }}
      className="col-start-2 row-span-2 row-start-1"
    >
      <CollapsibleCommentDrawer {...props} />
    </SidebarProvider>
  );
}

function CollapsibleCommentDrawer(passthroughProps: CommentDrawerProps) {
  const { t } = useTranslation("ui");
  const { open } = useSidebar();
  const { comments, revisions } = useAppSelector((state) => state.ticket);
  const dispatch = useAppDispatch();
  const reversedVersionIndex = useAppSelector(
    (state) => state.ticket.reversedVersionIndex,
  );
  const currentRevision = isNil(reversedVersionIndex)
    ? undefined
    : revisions[reversedVersionIndex];
  const resolvedComments = comments.filter(
    (c) =>
      c.boardId === currentRevision?._id &&
      c.disposition === TicketCommentDisposition.RESOLVED,
  );
  const dismissedComments = comments.filter(
    (c) =>
      c.boardId === currentRevision?._id &&
      c.disposition === TicketCommentDisposition.DISMISSED,
  );
  const deletedComments = comments.filter(
    (c) => c.boardId === currentRevision?._id && c.isDeleted === true,
  );
  const showResolvedComments = useAppSelector(
    (state) => state.ui.showResolvedComments,
  );
  const showDismissedComments = useAppSelector(
    (state) => state.ui.showDismissedComments,
  );
  const showDeletedComments = useAppSelector(
    (state) => state.ui.showDeletedComments,
  );
  const repliesCollapseCtx = useContext(RepliesCollapseContext);

  // Count of threads (top-level comments) for enabling/disabling global collapse/expand
  const threadCount = comments.filter(
    (c) => c.messages && c.messages.length > 0,
  ).length;
  const anyCollapsed =
    repliesCollapseCtx &&
    Object.values(repliesCollapseCtx.collapsed).some(Boolean);
  const allCollapsed =
    repliesCollapseCtx &&
    threadCount > 0 &&
    Object.values(repliesCollapseCtx.collapsed).length === threadCount &&
    Object.values(repliesCollapseCtx.collapsed).every(Boolean);

  return (
    <Sidebar side="right" data-testid="comments-sidebar">
      <SidebarHeader data-testid="comments-sidebar-header">
        <Flex gap="4" align="center" justify={open ? "between" : "center"}>
          <ChatTeardropText size={18} className="shrink-0" />

          <Flex
            flexGrow="1"
            gap="4"
            align="center"
            justify="between"
            className={cx({ hidden: !open })}
          >
            <Heading
              size="3"
              weight="medium"
              data-testid="comments-sidebar-header-label"
            >
              {t("tickets.index.comments")}
            </Heading>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger>
                <IconButton size="1" variant="ghost" color="gray">
                  <FunnelSimple />
                </IconButton>
              </DropdownMenu.Trigger>

              <DropdownMenu.Content>
                <DropdownMenu.Item
                  onClick={() => dispatch(toggleShowResolvedComments())}
                  disabled={resolvedComments.length === 0}
                >
                  {showResolvedComments
                    ? t("tickets.index.hide")
                    : t("tickets.index.show")}{" "}
                  {resolvedComments.length > 0 ? resolvedComments.length : ""}{" "}
                  resolved comment{resolvedComments.length === 1 ? "" : "s"}
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  onClick={() => dispatch(toggleShowDismissedComments())}
                  disabled={dismissedComments.length === 0}
                  className={cx({ hidden: !canShowDismissedComments() })}
                >
                  {showDismissedComments
                    ? t("tickets.index.hide")
                    : t("tickets.index.show")}{" "}
                  {dismissedComments.length > 0 ? dismissedComments.length : ""}{" "}
                  dismissed comment{dismissedComments.length === 1 ? "" : "s"}
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  onClick={() => dispatch(toggleShowDeletedComments())}
                  disabled={deletedComments.length === 0}
                  className={cx({ hidden: !canShowDeletedComments() })}
                >
                  {showDeletedComments
                    ? t("tickets.index.hide")
                    : t("tickets.index.show")}{" "}
                  {deletedComments.length > 0 ? deletedComments.length : ""}{" "}
                  deleted comment{deletedComments.length === 1 ? "" : "s"}
                </DropdownMenu.Item>
                <DropdownMenu.Separator />
                <DropdownMenu.Item
                  onClick={() => repliesCollapseCtx?.setAll(true)}
                  disabled={allCollapsed || threadCount === 0}
                >
                  {t("tickets.index.hideAllReplies")}
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  onClick={() => repliesCollapseCtx?.setAll(false)}
                  disabled={!anyCollapsed || threadCount === 0}
                >
                  {t("tickets.index.showAllReplies")}
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          </Flex>
        </Flex>
      </SidebarHeader>

      <SidebarContent>
        <Comments {...passthroughProps} />
      </SidebarContent>

      {/* Hover-to-toggle rail only — no floating circle button. Matches
          the main `AppSidebar` pattern: the rail itself is the affordance
          (resize-cursor + hover hairline) so we don't double up with a
          separate trigger button hanging off the edge of the panel. */}
      <SidebarRail
        aria-label={t("tickets.index.toggleComments")}
        title={t("tickets.index.toggleComments")}
      />
    </Sidebar>
  );
}
